/* @flow weak */
import _ from 'lodash';
import React from 'react';
import hoistStatics from 'hoist-non-react-statics';

import makePromiseQueryable from 'utils/makePromiseQueryable';

let firstRoute = true;

/**
 * @param Component The React Component to wrap. Rendering the component will block on the promises
                    in statics.routePrefetch to resolve.
 * @param Loading [optional] A React component to render while waiting for the promises to resolve.
 * @return React Component
 */

export default function(Component, blockingPromiseMakers = Component.routePrefetch, Loading) {
  // In karma tests, return the original component
  // so that we don't block on promises that won’t resolve.
  if (__TEST__) {
    return Component;
  }

  if (!blockingPromiseMakers) {
    console.error(`waitForPromises requires an array of blocking promise makers
      or routePrefetch on the Component`);
  }

  const [simplePromiseMakers, routeDependentPromiseMakers] =
    _.partition(blockingPromiseMakers, f => f.length === 0);

  class WrappedComponent extends React.Component {

    state: {
      loaded: boolean,
    };

    static contextTypes = {
      location: React.PropTypes.object.isRequired,
      params: React.PropTypes.object.isRequired,
      router: React.PropTypes.object.isRequired,
    };

    displayName = `WaitForPromises(${Component.displayName})`;

    constructor(props) {
      super(props);

      // wait until mounting to make these because some maker functions depend
      // on some data being loaded, such as the CurrentUser
      this.simplePromises = simplePromiseMakers
        .map(f => f())
        .filter(p => !!p) // filter when no promise made at all
        .map(makePromiseQueryable);

      // a promise can start as resolved, e.g. `Promise.resolve(foo)`
      const simplePromisesSettled = this.simplePromises.every(p => p.isSettled());

      // XXX bug in Babel if you inline this
      // `BabelLoaderError: SyntaxError: Invalid assignment value for "this.state".`
      const state = {
        loaded: simplePromisesSettled && routeDependentPromiseMakers.length === 0,
      };
      this.state = state;
    }

    componentWillMount() {
      this.route = window.location.pathname + window.location.search;

      this.fetchData();
    }

    componentDidMount() {
      this._isMounted = true;
    }

    componentWillUpdate() {
      this._isMounted = false;

      // Matching the original behavior of routePrefetch, which only blocks on the routePrefetch
      // promises when the route changes.
      if (this.route !== window.location.pathname + window.location.search) {
        this.route = window.location.pathname + window.location.search;
        this.fetchData();
      }
    }

    componentDidUpdate(prevProps, prevState) {
      if (firstRoute && this.state.loaded && !prevState.loaded) {
        firstRoute = false;
      }
    }

    componentWillUnmount() {
      this._isMounted = false;
    }

    // distateful hack for isMounted but this component will be going away
    _isMounted: boolean;
    route: string;
    simplePromises: Promise<*>[];

    fetchData() {
      const { location, params } = this.context;
      const { query } = location;
      const routeDependentPromises = routeDependentPromiseMakers
        .map(f => f(params, query)) // make promise using args
        .filter(p => !!p); // filter when no promise made

      const simplePromises = this.simplePromises.filter(p => !p.isSettled());

      const promises = routeDependentPromises.concat(simplePromises);

      // If there are no promises to resolve, we don't need to show Loading
      if (promises.length === 0) {
        if (!this.state.loaded) {
          this.setState({ loaded: true });
        }
      } else {
        this.setState({ loaded: false });
        Promise.all(promises)
        .then(() => {
          if (this._isMounted) {
            this.setState({ loaded: true });
          }
        })
        .catch(error => {
          // TODO: Figure out a better way to handle errors
          if (error.code === 'not_found') {
            this.context.router.replace('/not_found');
          }
          const badInitialRoute = firstRoute && error.statusCode === 404;
          if (badInitialRoute) {
            console.warn('Bad initial route', error);
          } else {
            console.error('Transition error:', error.stack ? error.stack : error);
          }
          throw new Error(error);
        });
      }
    }

    renderLoading() {
      if (Loading) {
        return <Loading {...this.props} />;
      } else if (__DEV__) {
        return (
          <div style={{ height: '100%', width: '100%', backgroundColor: '#fdd' }}>
            DEV: {Component.displayName} waiting for promises
          </div>
        );
      } else {
        return <div style={{ height: '100%', width: '100%', backgroundColor: '#f9f9f9' }} />;
      }
    }

    render() {
      if (this.state.loaded) {
        return <Component {...this.props} />;
      } else {
        return this.renderLoading();
      }
    }
  }

  return hoistStatics(WrappedComponent, Component);
}



// WEBPACK FOOTER //
// ./src/lib/waitForPromises.jsx